import { useCallback, useState } from 'react';
import {
  QueryFunction,
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

/**
 * Works the same as the useQuery hook, but the query needs to be executed
 * using the `runQuery` function by passing in the variables.
 * After the `runQuery` is called it works as a regular `useQuery` hook
 */
export function useLazyQuery<
  Variables extends Record<string, unknown>,
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: (variables: Variables) => TQueryKey,
  queryFn: (variables: Variables) => QueryFunction<TQueryFnData, TQueryKey>,
  options?: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  initialVariables?: Variables,
): [UseQueryResult<TData, TError>, (variables: Variables) => void] {
  const [queryVariables, setQueryVariables] = useState<Variables>(initialVariables as Variables);

  const queryOptions: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> = {
    ...(options || {}),
    enabled: !!queryVariables,
  };

  const query = useQuery(
    queryKey((queryVariables ?? {}) as Variables),
    queryFn((queryVariables ?? {}) as Variables),
    queryOptions,
  );

  const handleRunQuery = useCallback(
    (variables: Variables) => {
      setQueryVariables(variables);
    },
    [setQueryVariables],
  );

  return [query, handleRunQuery];
}
